/**
 * 微信浏览器调试工具
 * 用于在微信浏览器中调试样式和布局问题
 */

import { isWechat } from './mobile';

/**
 * 创建调试面板
 * @param {boolean} visible 是否默认可见
 */
const createDebugPanel = (visible = false) => {
  if (!isWechat()) return;
  
  // 创建调试面板容器
  const panel = document.createElement('div');
  panel.id = 'wechat-debug-panel';
  panel.style.cssText = `
    position: fixed;
    bottom: ${visible ? '0' : '-300px'};
    left: 0;
    width: 100%;
    height: 300px;
    background-color: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    z-index: 9999;
    transition: bottom 0.3s;
    overflow: auto;
    padding: 10px;
    box-sizing: border-box;
  `;
  
  // 创建调试面板标题
  const title = document.createElement('div');
  title.textContent = '微信浏览器调试面板';
  title.style.cssText = `
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 10px;
    padding-bottom: 5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  `;
  panel.appendChild(title);
  
  // 创建调试信息容器
  const infoContainer = document.createElement('div');
  infoContainer.id = 'wechat-debug-info';
  panel.appendChild(infoContainer);
  
  // 创建控制按钮
  const toggleButton = document.createElement('div');
  toggleButton.textContent = visible ? '隐藏' : '调试';
  toggleButton.style.cssText = `
    position: fixed;
    bottom: ${visible ? '300px' : '0'};
    right: 0;
    padding: 5px 10px;
    background-color: rgba(0, 0, 0, 0.8);
    color: #fff;
    font-size: 12px;
    z-index: 10000;
    cursor: pointer;
  `;
  
  // 添加切换面板显示/隐藏的事件
  toggleButton.addEventListener('click', () => {
    const isVisible = panel.style.bottom === '0px';
    panel.style.bottom = isVisible ? '-300px' : '0';
    toggleButton.style.bottom = isVisible ? '0' : '300px';
    toggleButton.textContent = isVisible ? '调试' : '隐藏';
  });
  
  // 将面板和按钮添加到文档中
  document.body.appendChild(panel);
  document.body.appendChild(toggleButton);
  
  return infoContainer;
};

/**
 * 更新调试信息
 * @param {HTMLElement} infoContainer 信息容器元素
 */
const updateDebugInfo = (infoContainer) => {
  if (!infoContainer) return;
  
  // 收集调试信息
  const info = {
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio
    },
    fontSize: {
      html: getComputedStyle(document.documentElement).fontSize,
      body: getComputedStyle(document.body).fontSize
    },
    scrollInfo: {
      scrollY: window.scrollY,
      scrollX: window.scrollX
    }
  };
  
  // 格式化并显示调试信息
  infoContainer.innerHTML = `
    <div style="margin-bottom: 10px;">
      <div><strong>User Agent:</strong></div>
      <div style="word-break: break-all;">${info.userAgent}</div>
    </div>
    <div style="margin-bottom: 10px;">
      <div><strong>Viewport:</strong></div>
      <div>Width: ${info.viewport.width}px</div>
      <div>Height: ${info.viewport.height}px</div>
      <div>Device Pixel Ratio: ${info.viewport.devicePixelRatio}</div>
    </div>
    <div style="margin-bottom: 10px;">
      <div><strong>Font Size:</strong></div>
      <div>HTML: ${info.fontSize.html}</div>
      <div>Body: ${info.fontSize.body}</div>
    </div>
    <div>
      <div><strong>Scroll:</strong></div>
      <div>X: ${info.scrollInfo.scrollX}px</div>
      <div>Y: ${info.scrollInfo.scrollY}px</div>
    </div>
  `;
};

/**
 * 初始化微信浏览器调试工具
 * @param {boolean} visible 是否默认可见
 * @param {boolean} enableDebug 是否启用调试
 */
export const initWechatDebug = (visible = false, enableDebug = process.env.NODE_ENV === 'development') => {
  if (!isWechat() || !enableDebug) return;
  
  // 创建调试面板
  const infoContainer = createDebugPanel(visible);
  
  // 初始更新调试信息
  updateDebugInfo(infoContainer);
  
  // 监听滚动和调整大小事件，更新调试信息
  window.addEventListener('scroll', () => updateDebugInfo(infoContainer));
  window.addEventListener('resize', () => updateDebugInfo(infoContainer));
  
  // 每秒更新一次调试信息
  setInterval(() => updateDebugInfo(infoContainer), 1000);
  
  console.log('微信浏览器调试工具已初始化');
};